import { useEffect, useState } from "react";
import { AlertTriangle, ShieldAlert, Loader2, Save, Radio } from "lucide-react";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";

const LEVEL_META = {
  NORMAL:  { label: "NORMAL",  color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",  dot: "bg-emerald-400",  desc: "Situazione operativa ordinaria. Nessuna minaccia identificata. Operazioni di routine." },
  ALPHA:   { label: "ALPHA",   color: "bg-yellow-500/15 text-yellow-300 border-yellow-500/40",    dot: "bg-yellow-400",   desc: "Minaccia generale, non specifica. Aumentare la consapevolezza situazionale. Segnalare comportamenti sospetti." },
  BRAVO:   { label: "BRAVO",   color: "bg-orange-500/15 text-orange-300 border-orange-500/40",    dot: "bg-orange-400",   desc: "Minaccia più prevedibile. Rafforzare i protocolli di sicurezza, ridurre esposizione non necessaria." },
  CHARLIE: { label: "CHARLIE", color: "bg-rose-500/15 text-rose-300 border-rose-500/40",          dot: "bg-rose-500",     desc: "Incidente in corso o minaccia probabile contro il Dipartimento. Massima allerta, dispiegamenti operativi immediati." },
  DELTA:   { label: "DELTA",   color: "bg-red-800/25 text-red-200 border-red-800/60",             dot: "bg-red-700",      desc: "Attacco imminente o in corso. Attivazione di tutte le procedure di emergenza. Blocco perimetrale." },
};
const LEVELS = ["NORMAL", "ALPHA", "BRAVO", "CHARLIE", "DELTA"];

export default function FpconPanel() {
  const [current, setCurrent] = useState(null);
  const [selected, setSelected] = useState("NORMAL");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/fpcon");
      setCurrent(data);
      setSelected(data.level);
      setNote(data.note || "");
    } catch (_) {
      toast.error("Impossibile caricare lo stato FPCON");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/fpcon", { level: selected, note: note.trim() });
      const disc = data.discord;
      if (disc && disc.ok === false) {
        toast.success(`Livello FPCON aggiornato a ${selected}. Discord embed: NON aggiornato (${disc.reason || "n/a"}).`);
      } else {
        toast.success(`Livello FPCON aggiornato a ${selected}. Discord embed sincronizzato.`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sheriff-gold/70" data-testid="fpcon-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Caricamento stato FPCON…
      </div>
    );
  }

  const currentMeta = current ? LEVEL_META[current.level] : LEVEL_META.NORMAL;
  const selectedMeta = LEVEL_META[selected] || LEVEL_META.NORMAL;

  return (
    <div className="space-y-6" data-testid="fpcon-panel">
      {/* CURRENT LEVEL — BANNER GIGANTE */}
      <div className={`rounded-2xl border p-8 sm:p-10 ${currentMeta.color} relative overflow-hidden`} data-testid="fpcon-current">
        {/* Watermark shield sullo sfondo */}
        <ShieldAlert className="absolute -right-6 -bottom-6 h-56 w-56 opacity-[0.06] pointer-events-none" />
        <div className="flex items-center gap-3 mb-2 relative">
          <div className={`h-3 w-3 rounded-full ${currentMeta.dot} animate-pulse`} />
          <div className="uppercase-label text-[10px] tracking-[0.3em] opacity-80 font-mono">FPCON Corrente · Force Protection Condition</div>
        </div>
        <div
          className="font-heading font-black leading-none tracking-tight text-[64px] sm:text-[96px] lg:text-[120px] mb-3 relative"
          style={{ textShadow: "0 6px 24px rgba(0,0,0,0.35)" }}
          data-testid="fpcon-current-level"
        >
          {current.level}
        </div>
        <p className="text-base sm:text-lg leading-relaxed opacity-90 max-w-3xl relative">{currentMeta.desc}</p>
        {current.note && (
          <div className="mt-5 text-sm bg-black/30 border border-white/10 rounded-lg px-4 py-3 max-w-3xl relative">
            <span className="uppercase-label text-[10px] tracking-widest opacity-70 block mb-1">Nota operativa</span>
            {current.note}
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest opacity-60 mt-5 font-mono relative">
          {current.updated_by ? `Aggiornato da ${current.updated_by}` : ""}
          {current.updated_at ? ` · ${new Date(current.updated_at).toLocaleString("it-IT")}` : ""}
        </div>
      </div>

      {/* SCALA FPCON — LEGENDA COMPLETA */}
      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6" data-testid="fpcon-scale">
        <div className="flex items-center gap-2 mb-4">
          <ShieldAlert className="h-4 w-4 text-sheriff-gold" />
          <h3 className="font-heading font-bold text-sm uppercase tracking-widest text-sheriff-gold">Scala FPCON — Livelli e Significato</h3>
        </div>
        <div className="space-y-2.5">
          {LEVELS.map((lv) => {
            const meta = LEVEL_META[lv];
            const isCurrent = current && current.level === lv;
            return (
              <div
                key={lv}
                className={`flex items-start gap-4 rounded-lg border px-4 py-3 transition-colors ${
                  isCurrent
                    ? `${meta.color} ring-1 ring-sheriff-gold/40`
                    : "border-sheriff-gold/10 bg-[#0A1F18]/50"
                }`}
                data-testid={`fpcon-scale-row-${lv.toLowerCase()}`}
              >
                <div className={`h-3 w-3 rounded-full ${meta.dot} mt-1.5 shrink-0 ${isCurrent ? "animate-pulse" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className={`font-heading font-black text-base tracking-wide ${isCurrent ? "" : "text-[#F5E6B8]"}`}>
                    {lv}
                    {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-widest opacity-70">· Attivo</span>}
                  </div>
                  <p className={`text-sm leading-relaxed mt-1 ${isCurrent ? "opacity-90" : "text-[#B8A269]/85"}`}>
                    {meta.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* SELECTOR */}
      <div className="bg-[#0F2620] border border-sheriff-gold/25 rounded-2xl p-6" data-testid="fpcon-selector">
        <div className="flex items-center gap-2 mb-4">
          <Radio className="h-4 w-4 text-sheriff-gold" />
          <h3 className="font-heading font-bold text-lg text-[#F5E6B8]">Aggiorna livello di allerta</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 mb-5">
          {LEVELS.map((lv) => {
            const meta = LEVEL_META[lv];
            const active = selected === lv;
            return (
              <button
                key={lv}
                type="button"
                onClick={() => setSelected(lv)}
                data-testid={`fpcon-level-btn-${lv.toLowerCase()}`}
                className={`px-3 py-3 rounded-xl border text-sm font-heading font-black uppercase tracking-widest transition-all ${
                  active
                    ? `${meta.color} ring-2 ring-offset-2 ring-offset-[#0F2620] ring-sheriff-gold/60 scale-[1.02]`
                    : "border-sheriff-gold/20 text-[#B8A269] hover:border-sheriff-gold/40 hover:text-sheriff-gold"
                }`}
              >
                <div className="flex items-center justify-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
                  {lv}
                </div>
              </button>
            );
          })}
        </div>

        <div className={`rounded-lg border px-4 py-3 mb-4 ${selectedMeta.color}`}>
          <div className="text-xs uppercase tracking-widest opacity-70 mb-1">Descrizione livello selezionato</div>
          <div className="text-sm leading-relaxed">{selectedMeta.desc}</div>
        </div>

        <div className="space-y-2 mb-4">
          <label className="text-xs uppercase tracking-widest text-sheriff-gold/70 font-mono">Nota operativa (facoltativa)</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder="Contesto o istruzioni operative aggiuntive per il personale…"
            data-testid="fpcon-note"
            className="bg-[#0A1F18] border-sheriff-gold/30 text-[#F5E6B8] placeholder:text-sheriff-gold/40"
          />
          <div className="text-[10px] text-sheriff-gold/40 text-right font-mono">{note.length}/500</div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="fpcon-save-btn"
            className="bg-sheriff-gold text-[#0A1F18] hover:bg-sheriff-gold/90 font-bold"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Aggiorna FPCON
          </Button>
          <div className="text-xs text-sheriff-gold/50 flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" /> L&apos;embed Discord viene sincronizzato automaticamente
          </div>
        </div>
      </div>
    </div>
  );
}
